import { chosenByLine, formatDay } from '@church/domain'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useAccess } from '../access/useAccess.ts'
import { songCatalogKeys, useSongCatalogStatus } from './useSongCatalog.ts'
import { hitView } from './useSongTypeahead.ts'

/**
 * Songs › Discover (docs/15-MUSIC-AND-AI.md): Apple Music's top Christian
 * and gospel songs, its Christian and Gospel curators' playlists, and what
 * ForHim's churches sing most this month. Anyone with the song library sees
 * it where Apple Music is set up; adding a song from it is a Songs Editor's.
 */

/** Discover is offered: Apple Music set up, and the song library theirs. */
export function useDiscoverAvailable() {
  const viewer = useAccess().hasSomewhere('songs', 'viewer')
  const status = useSongCatalogStatus()
  return { available: viewer && status.data?.enabled === true, ready: !status.isPending }
}

export function useDiscover() {
  const api = useApi()
  const access = useAccess()
  const { available } = useDiscoverAvailable()
  const query = useQuery({
    queryKey: songCatalogKeys.discover(),
    enabled: available,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services['song-catalog'].discover.get()
      if (error) throw error
      return data
    },
    // Apple's charts move daily; the server keeps them an hour.
    staleTime: 10 * 60_000,
  })
  const data = query.data
  return {
    ...query,
    available,
    /** Adding a recording to the library (+ Add). */
    canAdd: access.hasSomewhere('songs', 'editor'),
    top: data?.top?.map(hitView) ?? null,
    topError: data?.topError ?? null,
    shelves: data?.shelves ?? [],
    popular: data?.popular.map(hitView) ?? [],
    /**
     * "For this Sunday's sermon": the next plan the viewer can see (to this
     * Sunday) whose sermon was read, and what churches chose for its
     * themes — each row with "Chosen by 12 churches for Resurrection".
     * Null: no such plan, or nothing to show for it.
     */
    sermon: data?.sermon
      ? {
          planId: data.sermon.planId,
          themes: data.sermon.themes,
          /** "The Empty Tomb · Sunday Morning · Sun, Oct 25". */
          line: [
            data.sermon.sermonTitle ?? data.sermon.planTitle,
            data.sermon.serviceTypeName,
            formatDay(data.sermon.date),
          ]
            .filter(Boolean)
            .join(' · '),
          songs: data.sermon.songs.map((hit) => ({
            ...hitView(hit),
            chosenBy: chosenByLine(hit.chosenBy),
          })),
        }
      : null,
  }
}

/** One of Apple Music's playlists, opened from Discover (null: none open). */
export function useDiscoverPlaylist(playlistId: string | null) {
  const api = useApi()
  const { available } = useDiscoverAvailable()
  const query = useQuery({
    queryKey: songCatalogKeys.playlist(playlistId ?? ''),
    enabled: available && playlistId !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services['song-catalog']
        .playlists({ playlistId: playlistId ?? '' })
        .get()
      if (error) throw error
      return data
    },
    staleTime: 10 * 60_000,
  })
  return {
    ...query,
    playlist: query.data?.playlist ?? null,
    songs: query.data?.songs.map(hitView) ?? [],
  }
}
